import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import bindAll from 'lodash.bindall';

import GUI from '../containers/gui.jsx';
import LoginForm from '../components/auth/login-form.jsx';
import RegisterModal from '../components/auth/register-modal.jsx';
import MyStuffModal from '../components/auth/my-stuff-modal.jsx';
import ShareLinkModal from '../components/auth/share-link-modal.jsx';
import {
    getSession,
    login as apiLogin,
    logout as apiLogout,
    register as apiRegister,
    listMyProjects,
    listPublicProjects,
    getProjectMeta,
    patchProject,
    deleteProject
} from '../lib/auth-api';
import {setSession, clearSession} from '../reducers/session';
import {setProjectId} from '../reducers/project-state';
import {setProjectTitle} from '../reducers/project-title';

const origin = () => (typeof window !== 'undefined' ? window.location.origin : '');

const shareLinkFor = id => `${origin()}/p/${id}`;

const parsePlayPathId = () => {
    if (typeof window === 'undefined') return null;
    const match = window.location.pathname.match(/^\/p\/(\d+)\/?$/);
    return match ? match[1] : null;
};

class HostedGUI extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleLogin',
            'handleRegister',
            'handleLogout',
            'handleOpenRegistration',
            'handleCloseRegistration',
            'renderLogin',
            'handleShare',
            'handleCloseShare',
            'handleSeeCommunity',
            'handleOpenMyStuff',
            'handleCloseMyStuff',
            'handleOpenProject',
            'handleDeleteProject',
            'handleTogglePublic',
            'refreshProjectFlags'
        ]);
        this.state = {
            registerOpen: false,
            myStuffOpen: false,
            shareOpen: false,
            shareLink: '',
            isShared: false,
            isOwner: false,
            canRemix: false,
            isPlayPage: !!parsePlayPathId()
        };
    }
    componentDidMount () {
        const playId = this.props.initialSharedProjectId || parsePlayPathId();
        if (playId) {
            this.setState({isPlayPage: true});
            this.props.onSetProjectId(String(playId));
            if (!window.location.hash || window.location.hash === '#') {
                window.history.replaceState(null, '', `/p/${playId}`);
            }
        }

        getSession()
            .then(data => {
                if (data && data.session && data.session.user) {
                    this.props.onSetSession(data.session, data.permissions);
                } else {
                    this.props.onClearSession();
                }
            })
            .catch(() => this.props.onClearSession())
            .then(() => this.refreshProjectFlags());
    }
    componentDidUpdate (prevProps) {
        if (prevProps.projectId !== this.props.projectId ||
            prevProps.username !== this.props.username) {
            this.refreshProjectFlags();
        }
    }
    refreshProjectFlags () {
        const projectId = this.props.projectId;
        if (!projectId || projectId === '0') {
            this.setState({isShared: false, isOwner: !!this.props.username, canRemix: false});
            return;
        }
        getProjectMeta(projectId)
            .then(meta => {
                const isOwner = !!(this.props.username && meta.owner === this.props.username);
                this.setState({
                    isShared: !!meta.isPublic,
                    isOwner,
                    canRemix: !isOwner && !!meta.isPublic && !!this.props.username
                });
                if (meta.title) {
                    this.props.onSetProjectTitle(meta.title);
                }
            })
            .catch(() => {
                this.setState({isShared: false, isOwner: false, canRemix: false});
            });
    }
    handleDeleteProject (id) {
        return deleteProject(id).then(() => {
            // Do not reload the page: a full reload + canCreateNew would immediately
            // POST a fresh "Проект Scratch", which looks like delete failed.
            if (String(this.props.projectId) === String(id)) {
                this.props.onSetProjectId('0');
                if (typeof window !== 'undefined') {
                    window.history.replaceState(null, '', '/');
                    if (window.location.hash) {
                        window.location.hash = '';
                    }
                }
                this.setState({isShared: false, isOwner: true, canRemix: false});
            }
        });
    }
    handleTogglePublic (id, isPublic) {
        return patchProject(id, {isPublic}).then(meta => {
            if (String(this.props.projectId) === String(id)) {
                this.setState({isShared: !!meta.isPublic});
            }
            return meta;
        });
    }
    handleLogin (username, password) {
        return apiLogin(username, password).then(data => {
            this.props.onSetSession(data.session, data.permissions);
            this.refreshProjectFlags();
        });
    }
    handleRegister (username, password) {
        return apiRegister(username, password).then(data => {
            this.props.onSetSession(data.session, data.permissions);
            this.refreshProjectFlags();
        });
    }
    handleLogout () {
        return apiLogout()
            .catch(() => null)
            .then(() => {
                this.props.onClearSession();
                this.setState({isOwner: false, canRemix: false});
            });
    }
    handleOpenRegistration () {
        this.setState({registerOpen: true});
    }
    handleCloseRegistration () {
        this.setState({registerOpen: false});
    }
    renderLogin (loginProps) {
        return (
            <LoginForm
                onClose={loginProps.onClose}
                onSubmit={this.handleLogin}
            />
        );
    }
    handleShare () {
        const projectId = this.props.projectId;
        if (!projectId || projectId === '0' || !this.state.isOwner) return;
        const link = shareLinkFor(projectId);
        const openShareTab = () => {
            if (typeof window !== 'undefined') {
                window.open(link, '_blank', 'noopener,noreferrer');
            }
        };
        // Publish, open player in a new tab (keeps the editor usable), show copyable link.
        patchProject(projectId, {isPublic: true})
            .then(meta => {
                this.setState({
                    isShared: !!meta.isPublic,
                    shareOpen: true,
                    shareLink: link
                });
                openShareTab();
            })
            .catch(() => {
                this.setState({shareOpen: true, shareLink: link});
                openShareTab();
            });
    }
    handleCloseShare () {
        this.setState({shareOpen: false});
    }
    // Scratch GUI defaults to setPlayer(true) here — that traps the editor tab.
    handleSeeCommunity () {
        // no-op: share opens /p/:id in a new tab via handleShare
    }
    handleOpenMyStuff () {
        this.setState({myStuffOpen: true});
    }
    handleCloseMyStuff () {
        this.setState({myStuffOpen: false});
    }
    handleOpenProject (id) {
        this.setState({isPlayPage: false});
        this.props.onSetProjectId(id);
        if (typeof window !== 'undefined') {
            window.history.pushState(null, '', `/#${id}`);
            window.location.hash = id;
        }
    }
    render () {
        const loggedIn = !!this.props.username;
        const hasProject = this.props.projectId && this.props.projectId !== '0';
        const playPage = this.state.isPlayPage || this.props.isPlayerOnly;
        const canSave = !playPage && loggedIn && (!hasProject || this.state.isOwner);
        // Never auto-create on open/login/after-delete. New server projects are
        // created only when the user explicitly Saves (see project-state START_MANUAL_UPDATING).
        const canCreateNew = false;
        const canCreateCopy = !playPage && loggedIn && hasProject && this.state.isOwner;
        const canRemix = !playPage && this.state.canRemix;

        const {
            onSetSession, // eslint-disable-line no-unused-vars
            onClearSession, // eslint-disable-line no-unused-vars
            onSetProjectId, // eslint-disable-line no-unused-vars
            onSetProjectTitle, // eslint-disable-line no-unused-vars
            username, // eslint-disable-line no-unused-vars
            initialSharedProjectId, // eslint-disable-line no-unused-vars
            isPlayerOnly, // eslint-disable-line no-unused-vars
            ...guiProps
        } = this.props;

        return (
            <React.Fragment>
                <GUI
                    {...guiProps}
                    assetHost={origin()}
                    projectHost={`${origin()}/projects`}
                    canSave={canSave}
                    canCreateNew={canCreateNew}
                    enableAutoSave={false}
                    canCreateCopy={canCreateCopy}
                    canRemix={canRemix}
                    canEditTitle={canSave}
                    canShare={canSave && !!hasProject}
                    isShared={this.state.isShared}
                    isPlayerOnly={playPage}
                    showComingSoon={false}
                    backpackVisible={false}
                    renderLogin={playPage ? undefined : this.renderLogin}
                    onLogOut={this.handleLogout}
                    onOpenRegistration={this.handleOpenRegistration}
                    onShare={this.handleShare}
                    onSeeCommunity={this.handleSeeCommunity}
                    onOpenMyStuff={this.handleOpenMyStuff}
                    onClickLogo={() => {
                        window.location.href = '/';
                    }}
                />
                <RegisterModal
                    isOpen={this.state.registerOpen}
                    onClose={this.handleCloseRegistration}
                    onSubmit={this.handleRegister}
                />
                <MyStuffModal
                    isOpen={this.state.myStuffOpen}
                    loadMine={listMyProjects}
                    loadPublic={listPublicProjects}
                    onClose={this.handleCloseMyStuff}
                    onDeleteProject={this.handleDeleteProject}
                    onOpenProject={this.handleOpenProject}
                    onTogglePublic={this.handleTogglePublic}
                />
                <ShareLinkModal
                    isOpen={this.state.shareOpen}
                    link={this.state.shareLink}
                    onClose={this.handleCloseShare}
                />
            </React.Fragment>
        );
    }
}

HostedGUI.propTypes = {
    initialSharedProjectId: PropTypes.string,
    isPlayerOnly: PropTypes.bool,
    onClearSession: PropTypes.func,
    onSetProjectId: PropTypes.func,
    onSetProjectTitle: PropTypes.func,
    onSetSession: PropTypes.func,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    username: PropTypes.string
};

const mapStateToProps = state => ({
    projectId: state.scratchGui.projectState.projectId,
    username: state.session && state.session.session && state.session.session.user ?
        state.session.session.user.username : null
});

const mapDispatchToProps = dispatch => ({
    onSetSession: (session, permissions) => dispatch(setSession(session, permissions)),
    onClearSession: () => dispatch(clearSession()),
    onSetProjectId: id => dispatch(setProjectId(id)),
    onSetProjectTitle: title => dispatch(setProjectTitle(title))
});

export default connect(mapStateToProps, mapDispatchToProps)(HostedGUI);
